import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PublishIcon from "@mui/icons-material/Publish";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { alpha, Box, SvgIconProps, Tooltip, useTheme } from "@mui/material";
import { ComponentType } from "react";

export const VERSION_STATUS = {
	DRAFT: "D",
	SCHEDULED: "S",
	PUBLISHED: "P",
} as const;

export type VersionStatus =
	(typeof VERSION_STATUS)[keyof typeof VERSION_STATUS];

export const VERSION_STATUS_LABEL: Record<VersionStatus, string> = {
	[VERSION_STATUS.DRAFT]: "Draft",
	[VERSION_STATUS.SCHEDULED]: "Scheduled",
	[VERSION_STATUS.PUBLISHED]: "Published",
};

type StatusBadge = {
	Icon: ComponentType<SvgIconProps>;
	tone: "warning" | "info" | "success";
	testId: string;
};

const VERSION_STATUS_BADGE: Record<VersionStatus, StatusBadge> = {
	[VERSION_STATUS.DRAFT]: {
		Icon: EditOutlinedIcon,
		tone: "warning",
		testId: "version-icon-draft",
	},
	[VERSION_STATUS.SCHEDULED]: {
		Icon: ScheduleIcon,
		tone: "info",
		testId: "version-icon-scheduled",
	},
	[VERSION_STATUS.PUBLISHED]: {
		Icon: CheckCircleOutlineIcon,
		tone: "success",
		testId: "version-icon-published",
	},
};

export const StatusIcon = ({
	status,
	date,
	isCurrent = true,
}: {
	status: VersionStatus;
	date?: string;
	isCurrent?: boolean;
}) => {
	const theme = useTheme();

	const badge = VERSION_STATUS_BADGE[status];
	const { tone, testId } = badge;

	const isSuperseded = status === VERSION_STATUS.PUBLISHED && !isCurrent;
	const Icon = isSuperseded ? PublishIcon : badge.Icon;
	const base = isSuperseded
		? theme.palette.text.disabled
		: theme.palette[tone].main;
	const glyph = isSuperseded
		? theme.palette.text.disabled
		: theme.palette[tone].dark;

	const tooltip = isSuperseded
		? "Superseded"
		: status === VERSION_STATUS.SCHEDULED && date
			? `Scheduled for ${date}`
			: VERSION_STATUS_LABEL[status];

	return (
		<Tooltip title={tooltip} placement="top" arrow>
			<Box
				component="span"
				role="img"
				aria-label={tooltip}
				data-test-id={isSuperseded ? "version-icon-superseded" : testId}
				sx={{
					width: 22,
					height: 22,
					flexShrink: 0,
					borderRadius: "50%",
					display: "inline-flex",
					alignItems: "center",
					justifyContent: "center",
					bgcolor: alpha(base, 0.12),
					color: glyph,
				}}
			>
				<Icon sx={{ fontSize: 15 }} />
			</Box>
		</Tooltip>
	);
};
